import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { saveIntendedRoute } from '../utils/routeUtils';

/**
 * Signup / Pricing Page Component
 * Displays pricing information and creates Stripe checkout session
 */
const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // User can proceed to checkout if logged in, or will be redirected to auth if not

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Check if user is logged in - if not, redirect to register first
      const token = localStorage.getItem('auth_token');
      if (!token) {
        // Store intended route and redirect to auth (show register form)
        saveIntendedRoute('/signup');
        navigate('/auth?redirect=/signup');
        return;
      }

      // Create checkout session
      const response = await api.post('/billing/create-checkout-session', {
        lookup_key: 'thetraders_pro_monthly_v1',
      });

      if (response.data.success && response.data.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.url;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err: any) {
      console.error('Checkout session creation error:', err);
      
      // Handle 401 - user not authenticated
      if (err?.response?.status === 401) {
        navigate('/auth?redirect=/signup');
        return;
      }
      
      const errorMessage = err?.response?.data?.error || err?.message || 'Failed to start checkout. Please try again.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950">
      {/* Signup Hero Section */}
      <section className="text-center py-20 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Start Your 7-Day Free Trial
          </h1>
          <p className="text-lg text-gray-400 mb-8">
            One clear plan. All features included. No charges until your trial ends.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-8 relative">
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-cyan-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                Most Popular
              </span>
            </div>

            {/* Pricing Header */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">TheTraders Pro</h3>
              
              <div className="flex items-baseline justify-center mb-2">
                <span className="text-5xl font-bold text-white">$39.99</span>
                <span className="text-xl text-gray-400 ml-2">/ month</span>
              </div>
              
              <p className="text-sm text-gray-400">
                7-day free trial · Cancel anytime
              </p>
            </div>

            {/* Features List */}
            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">AI-generated trading policy suggestions</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Automated 3-trades-per-week workflow</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Portfolio-aware risk & position limits</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">Personal dashboard & trading analytics</span>
              </li>
            </ul>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-900/20 border border-red-500/50 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Stripe Checkout Form */}
            <form onSubmit={handleSubmit}>
              <input
                type="hidden"
                name="lookup_key"
                value="thetraders_pro_monthly_v1"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
              >
                {loading ? 'Processing...' : 'Start 7-Day Free Trial'}
              </button>
            </form>

            {/* Note */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Card required. You won't be charged until the trial ends.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Signup;

